/**
 * The deterministic validator.
 *
 * Everything a language model returns passes through here before it is allowed
 * anywhere near the recipe. A high self-reported confidence cannot bypass a
 * bound; unknown operations are dropped, not guessed at (PLANT.md §9.8).
 */

import {
  type CurvePoint,
  type LocalAdjustments,
  type MaskKind,
  type MaskLayer,
  type Recipe,
  defaultLocalAdjustments,
  defaultMask,
} from './recipe.ts';
import {
  OPERATIONS_BY_ID,
  type ScalarOperationSpec,
  clampToSpec,
  readPath,
  writePath,
} from './registry.ts';
import type { EditPlan, PlanOperation, PlanWarning } from './plan.ts';

export interface ValidationResult {
  plan: EditPlan;
  /** Human-readable notes about what was rejected or clamped. */
  rejections: string[];
}

const LOCAL_BOUNDS: Record<keyof LocalAdjustments, [number, number]> = {
  exposure: [-5, 5],
  contrast: [-100, 100],
  highlights: [-100, 100],
  shadows: [-100, 100],
  whites: [-100, 100],
  blacks: [-100, 100],
  temperature: [-100, 100],
  tint: [-100, 100],
  saturation: [-100, 100],
  vibrance: [-100, 100],
  clarity: [-100, 100],
  texture: [-100, 100],
  sharpness: [-100, 100],
  noise: [0, 100],
};

const MASK_KINDS: readonly MaskKind[] = ['radial', 'linear', 'luminance', 'color'];

function num(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function clamp(value: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, value));
}

function str(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback;
}

/** Normalises and bounds a curve: sorted, deduplicated, inside the unit square. */
export function sanitiseCurve(raw: unknown): CurvePoint[] | null {
  if (!Array.isArray(raw)) return null;
  const points = raw
    .filter((p): p is Record<string, unknown> => typeof p === 'object' && p !== null)
    .map((p) => ({ x: clamp(num(p.x, -1), 0, 1), y: clamp(num(p.y, -1), 0, 1) }))
    .filter((p) => Number.isFinite(p.x) && Number.isFinite(p.y))
    .sort((a, b) => a.x - b.x);

  // Drop points that share an x with the previous one; the spline needs a
  // strictly increasing domain.
  const unique: CurvePoint[] = [];
  for (const point of points) {
    const previous = unique[unique.length - 1];
    if (previous && Math.abs(previous.x - point.x) < 1e-4) continue;
    unique.push(point);
  }
  if (unique.length < 2) return null;
  if (unique.length > 16) return null;
  // Anchor the ends so the curve always spans the full range.
  if (unique[0].x > 0.001) unique.unshift({ x: 0, y: unique[0].y });
  if (unique[unique.length - 1].x < 0.999) {
    unique.push({ x: 1, y: unique[unique.length - 1].y });
  }
  return unique;
}

function sanitiseLocalAdjustments(raw: unknown): Partial<LocalAdjustments> {
  const out: Partial<LocalAdjustments> = {};
  if (typeof raw !== 'object' || raw === null) return out;
  const source = raw as Record<string, unknown>;
  for (const key of Object.keys(LOCAL_BOUNDS) as (keyof LocalAdjustments)[]) {
    const value = source[key];
    if (typeof value !== 'number' || !Number.isFinite(value)) continue;
    const [lo, hi] = LOCAL_BOUNDS[key];
    out[key] = clamp(value, lo, hi);
  }
  return out;
}

/**
 * Validates a raw planner payload into an `EditPlan`.
 *
 * `recipe` is the state the plan was computed against; it is used to enforce
 * per-operation `maxDelta` so a proposal cannot swing a control across its
 * whole range unless the operation allows it.
 */
export function validatePlan(raw: unknown, recipe: Recipe): ValidationResult {
  const rejections: string[] = [];
  const source = (typeof raw === 'object' && raw !== null ? raw : {}) as Record<string, unknown>;

  const operations: PlanOperation[] = [];
  const rawOperations = Array.isArray(source.operations) ? source.operations : [];

  for (const entry of rawOperations) {
    if (typeof entry !== 'object' || entry === null) {
      rejections.push('Dropped a malformed operation.');
      continue;
    }
    const item = entry as Record<string, unknown>;
    const id = str(item.op);
    const spec = OPERATIONS_BY_ID.get(id);

    if (!spec) {
      rejections.push(`Rejected unknown operation "${id || '(missing)'}".`);
      continue;
    }

    const reason = str(item.reason, 'No reason given.');

    if (spec.kind === 'scalar') {
      const requested = item.value;
      if (typeof requested !== 'number' || !Number.isFinite(requested)) {
        rejections.push(`Rejected ${spec.label}: value was not a number.`);
        continue;
      }
      const bounded = boundScalar(spec, requested, recipe, rejections);
      operations.push({ kind: 'scalar', op: id, value: bounded, reason });
      continue;
    }

    if (spec.kind === 'curve') {
      const points = sanitiseCurve(item.points);
      if (!points) {
        rejections.push(`Rejected ${spec.label}: curve needs 2–16 ordered points in 0..1.`);
        continue;
      }
      operations.push({ kind: 'curve', op: id, points, reason });
      continue;
    }

    // mask.add
    const maskKind = str(item.maskKind) as MaskKind;
    if (!MASK_KINDS.includes(maskKind)) {
      rejections.push(`Rejected local adjustment: unknown mask kind "${maskKind}".`);
      continue;
    }
    const adjustments = sanitiseLocalAdjustments(item.adjustments);
    if (Object.keys(adjustments).length === 0) {
      rejections.push('Rejected local adjustment: it carried no usable adjustments.');
      continue;
    }
    const geometry = (item as Record<string, unknown>) ?? {};
    operations.push({
      kind: 'mask',
      op: 'mask.add',
      name: str(item.name, 'Local adjustment').slice(0, 60),
      maskKind,
      radial: sanitiseRadial(geometry.radial),
      linear: sanitiseLinear(geometry.linear),
      luminance: sanitiseLuminance(geometry.luminance),
      color: sanitiseColorRange(geometry.color),
      invert: item.invert === true,
      feather: clamp(num(item.feather, 50), 0, 100),
      opacity: clamp(num(item.opacity, 100), 0, 100),
      adjustments,
      reason,
    });
  }

  const warnings: PlanWarning[] = (Array.isArray(source.warnings) ? source.warnings : [])
    .map((w): PlanWarning | null => {
      if (typeof w === 'string') return { message: w, severity: 'info' };
      if (typeof w === 'object' && w !== null) {
        const item = w as Record<string, unknown>;
        const message = str(item.message);
        if (!message) return null;
        return { message, severity: item.severity === 'caution' ? 'caution' : 'info' };
      }
      return null;
    })
    .filter((w): w is PlanWarning => w !== null);

  if (rejections.length > 0) {
    warnings.push({
      message: `${rejections.length} proposed change${rejections.length === 1 ? '' : 's'} did not pass validation and ${rejections.length === 1 ? 'was' : 'were'} discarded.`,
      severity: 'caution',
    });
  }

  const plan: EditPlan = {
    summary: str(source.summary, 'No summary provided.'),
    borrowedTraits: (Array.isArray(source.borrowedTraits) ? source.borrowedTraits : [])
      .filter((t): t is string => typeof t === 'string')
      .slice(0, 12),
    operations,
    warnings,
    protected: (Array.isArray(source.protected) ? source.protected : [])
      .filter((t): t is string => typeof t === 'string')
      .slice(0, 12),
    confidence: clamp(num(source.confidence, 0.5), 0, 1),
  };

  return { plan, rejections };
}

function boundScalar(
  spec: ScalarOperationSpec,
  requested: number,
  recipe: Recipe,
  rejections: string[],
): number {
  let value = clampToSpec(spec, requested);
  if (value !== requested) {
    rejections.push(
      `Clamped ${spec.label} from ${requested} to ${value} (allowed ${spec.min}…${spec.max}).`,
    );
  }
  if (spec.maxDelta !== undefined) {
    const currentRaw = readPath(recipe, spec.path);
    const current = typeof currentRaw === 'number' ? currentRaw : spec.neutral;
    const delta = value - current;
    if (Math.abs(delta) > spec.maxDelta) {
      const limited = current + Math.sign(delta) * spec.maxDelta;
      rejections.push(
        `Limited ${spec.label} to a ${spec.maxDelta}-unit move in one step (asked for ${Math.abs(delta).toFixed(2)}).`,
      );
      value = clampToSpec(spec, limited);
    }
  }
  return value;
}

function sanitiseRadial(raw: unknown) {
  if (typeof raw !== 'object' || raw === null) return undefined;
  const r = raw as Record<string, unknown>;
  return {
    cx: clamp(num(r.cx, 0.5), -0.5, 1.5),
    cy: clamp(num(r.cy, 0.5), -0.5, 1.5),
    rx: clamp(num(r.rx, 0.3), 0.02, 2),
    ry: clamp(num(r.ry, 0.3), 0.02, 2),
    angle: clamp(num(r.angle, 0), -180, 180),
  };
}

function sanitiseLinear(raw: unknown) {
  if (typeof raw !== 'object' || raw === null) return undefined;
  const r = raw as Record<string, unknown>;
  return {
    x1: clamp(num(r.x1, 0.5), -1, 2),
    y1: clamp(num(r.y1, 0.2), -1, 2),
    x2: clamp(num(r.x2, 0.5), -1, 2),
    y2: clamp(num(r.y2, 0.7), -1, 2),
  };
}

function sanitiseLuminance(raw: unknown) {
  if (typeof raw !== 'object' || raw === null) return undefined;
  const r = raw as Record<string, unknown>;
  const low = clamp(num(r.low, 0), 0, 1);
  const high = clamp(num(r.high, 0.5), 0, 1);
  return {
    low: Math.min(low, high),
    high: Math.max(low, high),
    lowFeather: clamp(num(r.lowFeather, 0.1), 0, 0.5),
    highFeather: clamp(num(r.highFeather, 0.1), 0, 0.5),
  };
}

function sanitiseColorRange(raw: unknown) {
  if (typeof raw !== 'object' || raw === null) return undefined;
  const r = raw as Record<string, unknown>;
  return {
    hue: ((num(r.hue, 25) % 360) + 360) % 360,
    hueRange: clamp(num(r.hueRange, 30), 5, 180),
    minSaturation: clamp(num(r.minSaturation, 0.08), 0, 1),
  };
}

/**
 * Applies selected operations to a recipe, returning a new recipe.
 * `selected` is the set of operation indexes the user accepted.
 */
export function applyPlan(
  recipe: Recipe,
  plan: EditPlan,
  selected: ReadonlySet<number> | null = null,
): Recipe {
  const next = structuredClone(recipe);
  plan.operations.forEach((operation, index) => {
    if (selected && !selected.has(index)) return;
    const spec = OPERATIONS_BY_ID.get(operation.op);
    if (!spec) return;

    if (operation.kind === 'scalar' && spec.kind === 'scalar') {
      writePath(next, spec.path, clampToSpec(spec, operation.value));
      return;
    }
    if (operation.kind === 'curve' && spec.kind === 'curve') {
      writePath(next, spec.path, operation.points.map((p) => ({ ...p })));
      return;
    }
    if (operation.kind === 'mask') {
      next.masks.push(maskFromPlan(operation, next.masks.length));
    }
  });
  return next;
}

function maskFromPlan(
  operation: Extract<PlanOperation, { kind: 'mask' }>,
  index: number,
): MaskLayer {
  const id = `ai-${index}-${Math.random().toString(36).slice(2, 8)}`;
  const mask = defaultMask(operation.maskKind, id, operation.name);
  mask.origin = 'ai';
  mask.rationale = operation.reason;
  mask.invert = operation.invert ?? false;
  mask.feather = operation.feather ?? mask.feather;
  mask.opacity = operation.opacity ?? mask.opacity;
  if (operation.radial) mask.radial = operation.radial;
  if (operation.linear) mask.linear = operation.linear;
  if (operation.luminance) mask.luminance = operation.luminance;
  if (operation.color) mask.color = operation.color;
  mask.adjustments = { ...defaultLocalAdjustments(), ...operation.adjustments };
  return mask;
}
